
import { useState, useEffect, useCallback } from 'react';

interface Project {
  id: string;
  image: string;
  title: string;
  location: string;
  category: string;
}

const projects: Project[] = [
  {
    id: 'p1',
    image:
      'https://readdy.ai/api/search-image?query=Modern%20renovated%20bathroom%20interior%20after%20water%20damage%20restoration%2C%20white%20tiles%2C%20contemporary%20fixtures%2C%20clean%20and%20bright%20space%2C%20professional%20renovation%20work%2C%20minimalist%20design%2C%20natural%20lighting%2C%20high-end%20residential%20bathroom%2C%20simple%20clean%20background&width=600&height=800&seq=portfolio-001&orientation=portrait',
    title: 'Remont Łazienki po Zalaniu',
    location: 'Zielona Góra, Centrum',
    category: 'Osuszanie + Remont',
  },
  {
    id: 'p2',
    image:
      'https://readdy.ai/api/search-image?query=Elegant%20living%20room%20interior%20after%20complete%20renovation%2C%20modern%20furniture%2C%20wooden%20flooring%2C%20large%20windows%2C%20bright%20and%20spacious%2C%20contemporary%20design%2C%20professional%20interior%20finishing%2C%20luxury%20residential%20space%2C%20simple%20clean%20background&width=600&height=800&seq=portfolio-002&orientation=portrait',
    title: 'Kompleksowe Wykończenie Salonu',
    location: 'Zielona Góra, Jędrzychów',
    category: 'Wykończenia Wnętrz',
  },
  {
    id: 'p3',
    image:
      'https://readdy.ai/api/search-image?query=Modern%20kitchen%20interior%20after%20water%20damage%20repair%2C%20white%20cabinets%2C%20granite%20countertops%2C%20stainless%20steel%20appliances%2C%20clean%20and%20functional%20space%2C%20professional%20renovation%2C%20contemporary%20kitchen%20design%2C%20simple%20clean%20background&width=600&height=800&seq=portfolio-003&orientation=portrait',
    title: 'Renowacja Kuchni',
    location: 'Zielona Góra, Łużyckie',
    category: 'Lokalizacja Wycieku',
  },
  {
    id: 'p4',
    image:
      'https://readdy.ai/api/search-image?query=Cozy%20bedroom%20interior%20after%20complete%20renovation%2C%20comfortable%20bed%2C%20soft%20lighting%2C%20modern%20furniture%2C%20clean%20walls%2C%20professional%20finishing%20work%2C%20peaceful%20atmosphere%2C%20residential%20bedroom%20design%2C%20simple%20clean%20background&width=600&height=800&seq=portfolio-004&orientation=portrait',
    title: 'Osuszanie i Malowanie Sypialni',
    location: 'Zielona Góra, Zastalowskie',
    category: 'Osuszanie + Malowanie',
  },
  {
    id: 'p5',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20water%20damage%20restoration%20in%20progress%2C%20industrial%20dehumidifiers%20working%20in%20flooded%20apartment%2C%20construction%20drying%20equipment%2C%20moisture%20removal%20process%2C%20bright%20room%20with%20equipment%2C%20realistic%20commercial%20photography%2C%20simple%20clean%20background&width=600&height=800&seq=portfolio-005&orientation=portrait',
    title: 'Osuszanie Mieszkania po Awarii',
    location: 'Zielona Góra, Centrum',
    category: 'Osuszanie',
  },
  {
    id: 'p6',
    image:
      'https://readdy.ai/api/search-image?query=Beautifully%20renovated%20hallway%20and%20corridor%20interior%2C%20modern%20lighting%20fixtures%2C%20clean%20white%20walls%2C%20wooden%20flooring%2C%20contemporary%20design%2C%20professional%20finishing%2C%20bright%20and%20welcoming%20entrance%20area%2C%20simple%20clean%20background&width=600&height=800&seq=portfolio-006&orientation=portrait',
    title: 'Wykończenie Korytarza',
    location: 'Nowa Sól',
    category: 'Wykończenia Wnętrz',
  },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

export default function Portfolio() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const isMobile = useIsMobile();

  const visibleCount = isMobile ? 1 : 4;
  const maxIndex = Math.max(0, projects.length - visibleCount);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  useEffect(() => {
    if (isPaused || projects.length <= visibleCount) return;
    const interval = setInterval(goNext, 4000);
    return () => clearInterval(interval);
  }, [isPaused, visibleCount, goNext]);

  // Reset index when switching between mobile/desktop
  useEffect(() => {
    setCurrentIndex(0);
  }, [isMobile]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const minSwipe = 50;
    if (distance > minSwipe) {
      goNext();
    } else if (distance < -minSwipe) {
      goPrev();
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
    <section id="portfolio" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="text-blue-600 text-sm font-semibold uppercase tracking-wider mb-3">
              Portfolio
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Nasze Realizacje
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl">
              Zobacz przykłady naszych projektów i przekonaj się o jakości naszej pracy
            </p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={goPrev}
              className="w-12 h-12 flex items-center justify-center rounded-full border-2 border-gray-300 text-gray-600 hover:border-blue-600 hover:text-blue-600 transition-all cursor-pointer"
              aria-label="Poprzedni"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={goNext}
              className="w-12 h-12 flex items-center justify-center rounded-full border-2 border-gray-300 text-gray-600 hover:border-blue-600 hover:text-blue-600 transition-all cursor-pointer"
              aria-label="Następny"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div
          className="overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              transform: `translateX(-${currentIndex * (100 / visibleCount)}%)`,
            }}
          >
            {projects.map((project) => (
              <div
                key={project.id}
                className="flex-shrink-0 px-2 md:px-3"
                style={{ width: `${100 / visibleCount}%` }}
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group">
                  <div className="relative h-64 md:h-72 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-bold text-gray-900 mb-2">
                      {project.title}
                    </h3>
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <i className="ri-map-pin-line mr-1"></i>
                      {project.location}
                    </div>
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">
                      {project.category}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        {projects.length > visibleCount && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`h-2.5 rounded-full transition-all cursor-pointer ${
                  i === currentIndex
                    ? 'bg-blue-600 w-8'
                    : 'bg-gray-300 hover:bg-gray-400 w-2.5'
                }`}
                aria-label={`Slajd ${i + 1}`}
              />
            ))}
          </div>
        )}

        {/* Mobile arrows */}
        <div className="flex md:hidden items-center justify-center gap-3 mt-6">
          <button
            onClick={goPrev}
            className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-gray-300 text-gray-600 hover:border-blue-600 hover:text-blue-600 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            onClick={goNext}
            className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-gray-300 text-gray-600 hover:border-blue-600 hover:text-blue-600 transition-all cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
